<h2 mat-dialog-title translate="CALL_ENTITIES.DIALOG_TITLE"></h2>
<mat-dialog-content>
  @let overview = (overview$ | async)?.data ?? null;
  @if (overview === null) {
    <div class="loading"><mat-spinner diameter="32"></mat-spinner></div>
  } @else {
    <p class="description" translate="CALL_ENTITIES.DIALOG_DESCRIPTION"></p>

    <section>
      <h3 translate="CALL_ENTITIES.ENTITIES_HEADING"></h3>
      <form class="add-entity" (ngSubmit)="addEntity()">
        <mat-form-field subscriptSizing="dynamic" appearance="outline">
          <mat-label translate="CALL_ENTITIES.NEW_ENTITY_NAME"></mat-label>
          <input matInput name="newEntityName" [(ngModel)]="newEntityName" maxlength="200" />
        </mat-form-field>
        <button mat-stroked-button type="submit" [disabled]="newEntityName.trim() === ''">
          <mat-icon>add</mat-icon>
          <span translate="CALL_ENTITIES.ADD_ENTITY"></span>
        </button>
        @if (overview.agendaEntityNamesToImport.length > 0) {
          <button mat-button type="button" (click)="importFromAgenda()">
            <mat-icon>playlist_add</mat-icon>
            <span>{{ 'CALL_ENTITIES.IMPORT_FROM_AGENDA' | translate: { count: overview.agendaEntityNamesToImport.length } }}</span>
          </button>
        }
      </form>

      @for (entity of overview.entities; track entity.id; let index = $index, first = $first, last = $last) {
        @let entityReadiness = readiness(overview, entity);
        <div class="entity">
          <div class="entity-header">
            <mat-form-field class="entity-name" subscriptSizing="dynamic" appearance="outline">
              <input
                matInput
                #nameInput
                [value]="entity.name"
                maxlength="200"
                [attr.aria-label]="'CALL_ENTITIES.ENTITY_NAME' | translate"
                (blur)="renameEntity(entity, nameInput.value)"
                (keydown.enter)="nameInput.blur()"
              />
            </mat-form-field>
            <button
              mat-icon-button
              [disabled]="first"
              (click)="moveEntity(overview.entities, index, -1)"
              [matTooltip]="'CALL_ENTITIES.MOVE_UP' | translate"
              [attr.aria-label]="'CALL_ENTITIES.MOVE_UP' | translate"
            >
              <mat-icon>arrow_upward</mat-icon>
            </button>
            <button
              mat-icon-button
              [disabled]="last"
              (click)="moveEntity(overview.entities, index, 1)"
              [matTooltip]="'CALL_ENTITIES.MOVE_DOWN' | translate"
              [attr.aria-label]="'CALL_ENTITIES.MOVE_DOWN' | translate"
            >
              <mat-icon>arrow_downward</mat-icon>
            </button>
            <button
              mat-icon-button
              (click)="deleteEntity(entity)"
              [matTooltip]="'CALL_ENTITIES.DELETE_ENTITY' | translate"
              [attr.aria-label]="'CALL_ENTITIES.DELETE_ENTITY' | translate"
            >
              <mat-icon>delete</mat-icon>
            </button>
          </div>

          <div class="intervals">
            <span class="intervals-label" translate="CALL_ENTITIES.DISCUSSED_AT"></span>
            <ng-container *ngTemplateOutlet="intervalEditor; context: { entity, templateItemId: null }"></ng-container>
          </div>

          <div class="entity-footer">
            <span class="status">{{ 'CALL_ENTITIES.STATUS.' + entity.minutesStatus | translate }}</span>
            @if (entityReadiness !== 'READY') {
              <span class="hint">{{ 'CALL_ENTITIES.READINESS.' + entityReadiness | translate }}</span>
            }
            <button mat-stroked-button [disabled]="entityReadiness !== 'READY' || entity.minutesStatus === 'ONGOING'" (click)="generateEntityMinutes(entity)">
              <mat-icon>auto_awesome</mat-icon>
              <span>{{ (entity.hasMinutes ? 'CALL_ENTITIES.REGENERATE_MINUTES' : 'CALL_ENTITIES.GENERATE_MINUTES') | translate }}</span>
            </button>
          </div>
        </div>
      } @empty {
        @if (hasEntitySpecificItems(overview)) {
          <p class="hint warning" translate="CALL_ENTITIES.ENTITY_SPECIFIC_ITEMS_WITHOUT_ENTITIES"></p>
        } @else {
          <p class="hint" translate="CALL_ENTITIES.NO_ENTITIES"></p>
        }
      }
    </section>

    <section>
      <h3 translate="CALL_ENTITIES.TEMPLATE_ITEMS_HEADING"></h3>
      <p class="description" translate="CALL_ENTITIES.TEMPLATE_ITEMS_DESCRIPTION"></p>
      @for (orderedItem of orderTemplateItems(overview.templateItems); track orderedItem.item.templateItemId) {
        @let item = orderedItem.item;
        @let isInherited = inheritsEntityScope(item, overview.templateItems);
        <div class="template-item" [style.padding-left.px]="orderedItem.depth * 20">
          <mat-checkbox
            [checked]="item.isEntitySpecific || isInherited"
            [disabled]="isInherited"
            (change)="setEntitySpecific(item, $event.checked)"
            [matTooltip]="isInherited ? ('CALL_ENTITIES.INHERITED_SCOPE' | translate) : ''"
          >
            {{ item.title }}
          </mat-checkbox>
          @if (item.isEntitySpecific && !isInherited && overview.entities.length > 0) {
            <mat-form-field class="applicable-entities" subscriptSizing="dynamic" appearance="outline">
              <mat-select
                multiple
                [value]="item.applicableCallEntityIds"
                [placeholder]="'CALL_ENTITIES.ALL_ENTITIES' | translate"
                (selectionChange)="setApplicableEntities(item, $event.value)"
                [attr.aria-label]="'CALL_ENTITIES.APPLIES_TO' | translate"
              >
                @for (entity of overview.entities; track entity.id) {
                  <mat-option [value]="entity.id">{{ entity.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
        </div>
        @if ((item.isEntitySpecific || isInherited) && overview.entities.length > 0) {
          <div class="item-entities" [style.padding-left.px]="orderedItem.depth * 20 + 32">
            @for (entity of entitiesOf(item, overview); track entity.id) {
              <div class="item-entity">
                <span class="item-entity-name">{{ entity.name }}</span>
                <ng-container *ngTemplateOutlet="intervalEditor; context: { entity, templateItemId: item.templateItemId }"></ng-container>
              </div>
            }
          </div>
        }
      } @empty {
        <p class="hint" translate="CALL_ENTITIES.NO_TEMPLATE_ITEMS"></p>
      }
    </section>
  }
</mat-dialog-content>
<mat-dialog-actions align="end">
  @let actionsOverview = (overview$ | async)?.data ?? null;
  @if (actionsOverview !== null && actionsOverview.entities.length > 0) {
    <button mat-stroked-button [disabled]="!canGenerateAny(actionsOverview)" (click)="generateAllEntityMinutes()">
      <mat-icon>auto_awesome</mat-icon>
      <span translate="CALL_ENTITIES.GENERATE_ALL_MINUTES"></span>
    </button>
  }
  <button mat-button mat-dialog-close translate="COMMON.CLOSE"></button>
</mat-dialog-actions>

<ng-template #intervalEditor let-entity="entity" let-templateItemId="templateItemId">
  <mat-chip-set>
    @for (interval of intervalsOf(entity, templateItemId); track interval.id) {
      <mat-chip (removed)="removeInterval(entity, interval)">
        {{ interval.startSeconds | timestamp }} – {{ interval.endSeconds | timestamp }}
        <button matChipRemove [attr.aria-label]="'CALL_ENTITIES.REMOVE_INTERVAL' | translate">
          <mat-icon>cancel</mat-icon>
        </button>
      </mat-chip>
    } @empty {
      <span class="hint" translate="CALL_ENTITIES.NO_INTERVALS"></span>
    }
  </mat-chip-set>
  @if (intervalDraft !== null && isDrafting(entity, templateItemId)) {
    <form class="interval-draft" (ngSubmit)="saveIntervalDraft(entity, templateItemId)">
      <mat-form-field subscriptSizing="dynamic" appearance="outline">
        <mat-label translate="CALL_ENTITIES.INTERVAL_START"></mat-label>
        <input matInput name="start" [(ngModel)]="intervalDraft.start" placeholder="00:00:00" />
      </mat-form-field>
      <mat-form-field subscriptSizing="dynamic" appearance="outline">
        <mat-label translate="CALL_ENTITIES.INTERVAL_END"></mat-label>
        <input matInput name="end" [(ngModel)]="intervalDraft.end" placeholder="00:00:00" />
      </mat-form-field>
      <button mat-stroked-button type="submit" translate="COMMON.SAVE"></button>
      <button mat-button type="button" (click)="cancelIntervalDraft()" translate="COMMON.CANCEL"></button>
    </form>
    @if (intervalDraft.isInvalid) {
      <p class="hint warning" translate="CALL_ENTITIES.INVALID_INTERVAL"></p>
    }
  } @else {
    <button mat-button (click)="startIntervalDraft(entity, templateItemId)">
      <mat-icon>schedule</mat-icon>
      <span translate="CALL_ENTITIES.ADD_INTERVAL"></span>
    </button>
  }
</ng-template>
